import React from "react";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, DatePicker, Select, Space, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import { useProductsList } from "~/modules/Dashboard/AppStats/hooks/useProductsList";

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  firstPurchaseFrom: string | null;
  firstPurchaseTo: string | null;
  onFirstPurchaseRangeChange: (from: string | null, to: string | null) => void;
}

export const ProductSequenceFilter: React.FC<Props> = ({
  value,
  onChange,
  firstPurchaseFrom,
  firstPurchaseTo,
  onFirstPurchaseRangeChange,
}) => {
  const { t } = useTranslation("products");
  const { groupedList: productsGroupedList, isLoading } = useProductsList();

  const updateAt = (idx: number, productId: string | undefined) => {
    const next = [...value];
    if (productId) {
      next[idx] = productId;
    } else {
      next.splice(idx, 1);
    }
    onChange(next.filter(Boolean));
  };

  const removeAt = (idx: number) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  const addSlot = () => {
    onChange([...value, ""]);
  };

  const slots = value.length > 0 ? value : [""];

  const rangeValue: [Dayjs | null, Dayjs | null] | null =
    firstPurchaseFrom || firstPurchaseTo
      ? [firstPurchaseFrom ? dayjs(firstPurchaseFrom) : null, firstPurchaseTo ? dayjs(firstPurchaseTo) : null]
      : null;

  return (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t("customersTab.filters.sequenceHint")}
      </Typography.Text>

      <Space wrap>
        {slots.map((productId, idx) => (
          <Space key={idx} size={4}>
            <Typography.Text strong>{idx + 1}.</Typography.Text>
            <Select
              loading={isLoading}
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder={t("customersTab.filters.selectProduct")}
              style={{ minWidth: 220 }}
              popupMatchSelectWidth={false}
              value={productId || undefined}
              onChange={(val) => updateAt(idx, val)}
              options={productsGroupedList}
            />
            {value.length > 0 ? (
              <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => removeAt(idx)} />
            ) : null}
          </Space>
        ))}
        <Button icon={<PlusOutlined />} onClick={addSlot} type="dashed" size="small">
          {t("customersTab.filters.addProduct")}
        </Button>
      </Space>

      <Space size={8} align="center" wrap>
        <Typography.Text style={{ fontSize: 12 }}>{t("customersTab.filters.firstPurchaseLabel")}:</Typography.Text>
        <DatePicker.RangePicker
          allowEmpty={[true, true]}
          value={rangeValue as [Dayjs, Dayjs] | null}
          onChange={(dates) => {
            const from = dates?.[0]?.startOf("day").toISOString() ?? null;
            const to = dates?.[1]?.endOf("day").toISOString() ?? null;
            onFirstPurchaseRangeChange(from, to);
          }}
          format="DD.MM.YYYY"
        />
      </Space>
    </Space>
  );
};
